import { DOCUMENT } from '@angular/common'
import { computed, inject, Injectable, Signal } from '@angular/core'
import { getDownloadURL, ref, Storage } from '@angular/fire/storage'
import { Observable, of } from 'rxjs'
import { map, shareReplay, switchMap, take } from 'rxjs/operators'
import { AppUser, UserVO } from '../../../../domain/user/app-user.model'
import { getDefaultShopSettings, ShopSettings } from '../../../../domain/user/shop-settings.model'
import { ShopSettingsFirestoreService } from '../../core/firestore-services/shop-settings-firestore.service'
import { ShopService } from '../../core/services/shop.service'
import { AuthService } from './auth.service'
import { toSignal } from '@angular/core/rxjs-interop'
import { AppUserCurrency } from '../../../../domain/price-categories'

@Injectable({
	providedIn: 'root',
})
export class ShopSettingsService {
	private readonly shopService = inject(ShopService)
	private readonly authService = inject(AuthService)
	private readonly shopSettingsFirestoreService = inject(ShopSettingsFirestoreService)
	private readonly storage = inject(Storage)
	private readonly document = inject<Document>(DOCUMENT)

	readonly currentUserShopSettingsChanges$: Observable<ShopSettings | null> = this.authService.appUserChanges$.pipe(
		switchMap((user) => {
			if (!user) {
				return of(null)
			}
			return this.shopSettingsFirestoreService.observeValues(this.getSettingsId(user))
		}),
		shareReplay(1),
	)
	readonly currentUserShopSettings$: Observable<ShopSettings | null> = this.currentUserShopSettingsChanges$.pipe(
		take(1),
	)

	/** Returns current shop settings
	 * It searches for settings in this order:
	 * 1. If route matches a public shop, return its settings
	 * 2. If route is private, return current user's shop-settings
	 * 3. If user is not logged in, return default shop-settings (todo: check if this is needed - might never be the case)
	 * 4. If role is seller, settingsUid is a {@link UserVO} so we access .uid
	 */
	readonly shopSettingsChanges$: Observable<ShopSettings> = this.shopService.routeShopUrlChanges$.pipe(
		switchMap((shopUrl) =>
			shopUrl ? this.shopSettingsFirestoreService.getByShopUrl(shopUrl) : this.currentUserShopSettingsChanges$,
		),
		map((settings) => settings ?? getDefaultShopSettings()),
		shareReplay(1),
	)
	readonly shopSettings$: Observable<ShopSettings> = this.shopSettingsChanges$.pipe(take(1))
	readonly shopSettings: Signal<ShopSettings | undefined> = toSignal(this.shopSettingsChanges$)

	readonly currency: Signal<AppUserCurrency> = computed(() => this.shopSettings()?.currency ?? AppUserCurrency.RON)

	readonly logoUrl1$: Observable<string> = this.shopSettingsChanges$.pipe(
		switchMap((settings) => getDownloadURL(ref(this.storage, settings.logoUrl1 || getDefaultShopSettings().logoUrl1))),
		shareReplay(1),
	)
	readonly logoUrl2$: Observable<string> = this.shopSettingsChanges$.pipe(
		switchMap((settings) => getDownloadURL(ref(this.storage, settings.logoUrl2 || getDefaultShopSettings().logoUrl2))),
		shareReplay(1),
	)

	readonly brandLogoUrl: Signal<string | undefined> = toSignal(this.logoUrl1$)

	private readonly defaultSettings: ShopSettings = getDefaultShopSettings()

	applySettings(settings: ShopSettings): void {
		this.document.documentElement.style.setProperty(
			'--aps-color-primary',
			settings.primary || this.defaultSettings.primary,
		)
		this.document.documentElement.style.setProperty(
			'--aps-color-secondary',
			settings.secondary || this.defaultSettings.secondary,
		)
		this.document.documentElement.style.setProperty('--aps-color-other', settings.other || this.defaultSettings.other)
		this.document.documentElement.style.setProperty(
			'--aps-color-background',
			settings.background || this.defaultSettings.background,
		)
	}

	private getSettingsId(appUser: AppUser): string {
		if (this.isUserVO(appUser.settingsUid)) {
			return appUser.settingsUid.uid
		}

		if (!appUser.settingsUid) {
			throw new Error('User settingsUid is undefined')
		}

		return appUser.settingsUid
	}

	/** @deprecated This usecase is deprecated, keeping it for backwards compatibility until a migration is run */
	private isUserVO(settingsUid: string | UserVO): settingsUid is UserVO {
		return typeof settingsUid === 'object'
	}
}
